/**
 * Serializer utilities for Slate editor content
 *
 * This module provides utilities for serializing Slate editor content,
 * particularly for removing comment nodes from serialized JSON.
 *
 * Use this when persisting editor content to remove Velt comment elements
 * while preserving the original text content.
 */
import { Constants } from '@/constants/common';
/** Use the element's canonical type string */
const COMMENT_TYPE = Constants.ELEMENT_TYPE.VELT_COMMENT;
/**
 * Export JSON without comment nodes.
 *
 * This function takes Slate editor content and removes all comment nodes,
 * unwrapping their children and merging adjacent text nodes with same formatting.
 *
 * @param content - Slate editor content (Descendant[])
 * @returns Serialized content without comment nodes
 */
export const exportJSONWithoutComments = (content) => {
    // 1) Strip comment nodes from the content
    const strippedContent = stripCommentsFromContent(content);
    // 2) Normalize adjacent text nodes created by unwrapping
    const normalizedContent = normalizeTextNodesInContent(strippedContent);
    return normalizedContent;
};
/* -------------------- internals (strictly typed) -------------------- */
const stripCommentsFromContent = (content) => {
    return stripFromChildrenArray(content);
};
/**
 * Flattens comment nodes inside a children array and recurses.
 */
const stripFromChildrenArray = (children) => {
    const out = [];
    for (const child of children) {
        if (isElementNode(child) && child.type === COMMENT_TYPE) {
            // Unwrap: push processed grandchildren directly
            const flattened = stripFromChildrenArray(child.children);
            out.push(...flattened);
            continue;
        }
        out.push(stripCommentsFromNode(child));
    }
    return out;
};
const stripCommentsFromNode = (node) => {
    if (isElementNode(node)) {
        const nextChildren = stripFromChildrenArray(node.children);
        return Object.assign(Object.assign({}, node), { children: nextChildren });
    }
    return node;
};
/* -------------------- normalization (strictly typed) -------------------- */
const normalizeTextNodesInContent = (content) => {
    const normalizedKids = content.map(normalizeTextNodesInNode);
    return mergeAdjacentTextNodes(normalizedKids);
};
const normalizeTextNodesInNode = (node) => {
    if (isElementNode(node)) {
        const normalizedKids = node.children.map(normalizeTextNodesInNode);
        return Object.assign(Object.assign({}, node), { children: mergeAdjacentTextNodes(normalizedKids) });
    }
    return node;
};
const mergeAdjacentTextNodes = (children) => {
    var _a, _b;
    if (children.length < 2)
        return children;
    const merged = [];
    let i = 0;
    while (i < children.length) {
        const cur = children[i];
        if (isTextNode(cur)) {
            // Skip empty text nodes (artifacts from Slate's split operation)
            if (cur.text === '') {
                i++;
                continue;
            }
            let text = (_a = cur.text) !== null && _a !== void 0 ? _a : '';
            let j = i + 1;
            while (j < children.length) {
                const nxt = children[j];
                // Skip empty text nodes
                if (isTextNode(nxt) && nxt.text === '') {
                    j++;
                    continue;
                }
                if (isTextNode(nxt) && haveSameTextFormat(cur, nxt)) {
                    text += (_b = nxt.text) !== null && _b !== void 0 ? _b : '';
                    j++;
                }
                else {
                    break;
                }
            }
            merged.push(Object.assign(Object.assign({}, cur), { text }));
            i = j;
        }
        else {
            merged.push(cur);
            i++;
        }
    }
    // Ensure at least one child for element nodes
    return merged.length > 0 ? merged : [{ text: '' }];
};
/* -------------------- type guards & equality -------------------- */
const isElementNode = (node) => {
    // Slate Element nodes always have a 'children' array and 'type' property
    return (Object.prototype.hasOwnProperty.call(node, 'children') &&
        Object.prototype.hasOwnProperty.call(node, 'type'));
};
const isTextNode = (node) => {
    return Object.prototype.hasOwnProperty.call(node, 'text');
};
const haveSameTextFormat = (a, b) => {
    return (a.bold === b.bold &&
        a.italic === b.italic &&
        a.underline === b.underline);
};
